// ┌──────────────────────────────────────────────────────────────────────────┐
// │  Rating history reconstruction                                             │
// │                                                                            │
// │  Only each player's CURRENT Elo is stored, so we recreate rating history   │
// │  by replaying every match in chronological order through the same Elo      │
// │  formula the backend uses (K-factor by games played, expected score,       │
// │  600–1400 clamp, 1000 baseline). A snapshot of all ratings is captured at  │
// │  the end of each tournament, giving one x-axis point per tournament.       │
// └──────────────────────────────────────────────────────────────────────────┘

const BASE_ELO = 1000;

const kFactor = (games) => (games < 30 ? 32 : 16);

const expectedScore = (a, b) => 1 / (1 + Math.pow(10, (b - a) / 400));

const newElo = (cur, expected, actual, games) =>
  Math.max(
    600,
    Math.min(1400, Math.round(cur + kFactor(games) * (actual - expected))),
  );

function applyMatch(elos, games, winner, loser) {
  const wElo = elos[winner] ?? BASE_ELO;
  const lElo = elos[loser] ?? BASE_ELO;
  const wGames = games[winner] ?? 0;
  const lGames = games[loser] ?? 0;

  elos[winner] = newElo(wElo, expectedScore(wElo, lElo), 1, wGames);
  elos[loser] = newElo(lElo, expectedScore(lElo, wElo), 0, lGames);
  games[winner] = wGames + 1;
  games[loser] = lGames + 1;
}

// Replay results → ordered snapshots, one per tournament (in completion order).
// Each snapshot: { tournamentId, tournamentName, elos: { name: rating } }.
export function buildRatingTimeline(results) {
  const matches = [...(results || [])].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
  );

  const remaining = {};
  for (const m of matches)
    remaining[m.tournamentId] = (remaining[m.tournamentId] || 0) + 1;

  const elos = {};
  const games = {};
  const participants = {}; // tournamentId -> Set of names who played in it
  const snapshots = [];

  for (const m of matches) {
    applyMatch(elos, games, m.winnerName, m.loserName);
    const set = (participants[m.tournamentId] ||= new Set());
    set.add(m.winnerName);
    set.add(m.loserName);
    remaining[m.tournamentId] -= 1;
    if (remaining[m.tournamentId] === 0) {
      snapshots.push({
        tournamentId: m.tournamentId,
        tournamentName: m.tournamentName,
        elos: { ...elos },
        participants: Array.from(set),
      });
    }
  }

  return snapshots;
}

// Turn the timeline into recharts rows for the selected players.
//
// Each player's line only spans the tournaments from their FIRST participation
// to their LAST. Before they ever played they weren't registered (no point);
// after they stop, the line ends (no misleading flat tail). Across gaps within
// their active span, their rating carries forward (retained, like chess).
export function buildChartData(timeline, selected) {
  const first = {};
  const last = {};
  timeline.forEach((snap, i) => {
    const playing = new Set(snap.participants);
    selected.forEach((n) => {
      if (playing.has(n)) {
        if (first[n] === undefined) first[n] = i;
        last[n] = i;
      }
    });
  });

  return timeline.map((snap, i) => {
    const row = { label: snap.tournamentName };
    selected.forEach((n) => {
      const active = first[n] !== undefined && i >= first[n] && i <= last[n];
      row[n] = active ? (snap.elos[n] ?? null) : null;
    });
    return row;
  });
}
